import { useCallback, useRef, useState } from 'react';

export interface EmailComposeAttachment {
  id: string;
  file: File;
}

export function formatEmailAttachmentSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function useEmailAttachmentPicker() {
  const inputRef = useRef<HTMLInputElement>(null);
  const [attachments, setAttachments] = useState<EmailComposeAttachment[]>([]);

  const openPicker = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const onFilesSelected = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files?.length) return;

    const next = Array.from(files).map((file) => ({
      id: `${file.name}-${file.size}-${file.lastModified}`,
      file,
    }));

    setAttachments((prev) => {
      const seen = new Set(prev.map((item) => item.id));
      return [...prev, ...next.filter((item) => !seen.has(item.id))];
    });

    event.target.value = '';
  }, []);

  const removeAttachment = useCallback((id: string) => {
    setAttachments((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const clearAttachments = useCallback(() => {
    setAttachments([]);
  }, []);

  return {
    inputRef,
    attachments,
    openPicker,
    onFilesSelected,
    removeAttachment,
    clearAttachments,
  };
}
